import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import { isPromoCodeEnabled } from "@circle-react/helpers/communityHelpers";
import { useMatches, useLocation } from "@tanstack/react-router";
import type { ReactNode } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";

import { useIsMemberAreaAppShellEnabled } from "@/react/hooks/useIsMemberAreaAppShellEnabled";

declare module "@tanstack/react-router" {
  interface StaticDataRouteOption {
    shellMode?: "canvas-only";
  }
}

interface ShellBypassContextValue {
  isBypassed: boolean;
  isPageChromeHidden: boolean;
  isPromoLockedToPlansPage: boolean;
  releaseBypass: () => void;
  releaseHidePageChrome: () => void;
  requestBypass: () => void;
  requestHidePageChrome: () => void;
}

const ShellBypassContext = createContext<ShellBypassContextValue | null>(null);
ShellBypassContext.displayName = "ShellBypassContext";

/**
 * Derives iframe bypass from URL search params so the shell can render
 * `CanvasOnlyContent` from its very first render. Without this, iframe-embed
 * routes (`?iframe=true`) pay a heavy mount→unmount→remount cycle: the shell
 * mounts `ShellNativeContent` (Sidebar + RailbarContainer + Workspace tree)
 * first, then the page's `useEffect` calls `setEmptyLayout()`, which mounts
 * `EmptyLayout`, which calls `useShellBypass()` via `useLayoutEffect`, which
 * flips `isCanvasOnly` true — at which point the entire ShellNativeContent
 * subtree (including the page itself, because its parent changes) remounts
 * inside `CanvasOnlyContent`. SideBar queries fire and get thrown away, the
 * page resets all its React Query state, and on small screens the user sees
 * a visible content flash.
 *
 * Honors `?iframe=true` (the matching condition mirrors `useIsIframeEmbed` so
 * both code paths agree) and routes that declare `staticData.shellMode ===
 * "canvas-only"` (read synchronously from the active TanStack matches, so the
 * CircleAI page routes render canvas-only from their first render instead of
 * flipping via `useShellBypass()` and remounting). Backend-driven minimal layout
 * (`useBackendProps().minimal_layout`) and React-driven shared-post minimal
 * still flow through the deferred `setMinimalLayout()` path because they
 * don't have a URL signal we can read synchronously here.
 *
 * URL-driven bypass is kept separate from `useShellBypass()` requests so
 * navigation can clear iframe bypass naturally while still allowing dynamic
 * layout components to request bypass when needed.
 *
 * Long-term, the entire bypass mechanism should go away in favor of
 * route-level layout selection (TanStack Router layout routes):
 *   /_shell/* → AppShellLayout, /_embed/* → EmbedLayout, /_minimal/* → MinimalLayout.
 * At that point pages no longer call `setEmptyLayout`/`setMinimalLayout`,
 * DynamicLayout disappears, and so do `ShellBypassProvider`/`useShellBypass`.
 * See the perf investigation in PR description for the migration plan.
 */
const isIframeBypassSearch = (search: string): boolean => {
  const params = new URLSearchParams(search);
  return params.get("iframe") === "true";
};

export const ShellBypassProvider = ({ children }: { children: ReactNode }) => {
  const search = useLocation({ select: location => location.searchStr });
  const { currentCommunity } = usePunditUserContext();
  // `promotion_enabled` (backed by a coupon's `lock_to_plans_page`) is only true
  // while the community is on trial with a locking coupon. It pins the admin to
  // the plans page; hiding the shell's page chrome here removes the v4 nav
  // affordances that would otherwise let them leave, mirroring how the v3 layout
  // already suppresses its chrome via `isPromoCodeEnabled`. It flips back off
  // automatically once they purchase a paid plan (no longer on trial).
  const isPromoLockedToPlansPage = isPromoCodeEnabled(currentCommunity);
  const [requestedBypassCount, setRequestedBypassCount] = useState(0);
  const [hidePageChromeCount, setHidePageChromeCount] = useState(0);
  const isIframeBypassed = useMemo(
    () => isIframeBypassSearch(search),
    [search],
  );
  const isRouteBypassed = useMatches({
    select: matches =>
      matches.some(match => match.staticData.shellMode === "canvas-only"),
  });

  const requestBypass = useCallback(() => {
    setRequestedBypassCount(c => c + 1);
  }, []);

  const releaseBypass = useCallback(() => {
    setRequestedBypassCount(c => Math.max(0, c - 1));
  }, []);

  const requestHidePageChrome = useCallback(() => {
    setHidePageChromeCount(c => c + 1);
  }, []);

  const releaseHidePageChrome = useCallback(() => {
    setHidePageChromeCount(c => Math.max(0, c - 1));
  }, []);

  const value = useMemo(
    () => ({
      isBypassed:
        isIframeBypassed || isRouteBypassed || requestedBypassCount > 0,
      isPageChromeHidden: isPromoLockedToPlansPage || hidePageChromeCount > 0,
      isPromoLockedToPlansPage,
      requestBypass,
      releaseBypass,
      requestHidePageChrome,
      releaseHidePageChrome,
    }),
    [
      isIframeBypassed,
      isRouteBypassed,
      isPromoLockedToPlansPage,
      requestedBypassCount,
      hidePageChromeCount,
      requestBypass,
      releaseBypass,
      requestHidePageChrome,
      releaseHidePageChrome,
    ],
  );

  return (
    <ShellBypassContext.Provider value={value}>
      {children}
    </ShellBypassContext.Provider>
  );
};

export const useIsShellBypassed = (): boolean => {
  const ctx = useContext(ShellBypassContext);
  return ctx?.isBypassed ?? false;
};

export const useIsPageChromeHidden = (): boolean => {
  const ctx = useContext(ShellBypassContext);
  return ctx?.isPageChromeHidden ?? false;
};

export const useIsPromoLockedToPlansPage = (): boolean => {
  const ctx = useContext(ShellBypassContext);
  return ctx?.isPromoLockedToPlansPage ?? false;
};

export const useIsInsideCircleAIShell = (): boolean => {
  const ctx = useContext(ShellBypassContext);
  return ctx !== null && !ctx.isBypassed;
};

export const useIsCircleAIShellMounted = (): boolean =>
  useContext(ShellBypassContext) !== null;

export const useShellBypass = () => {
  const ctx = useContext(ShellBypassContext);
  const isMemberAreaAppShellEnabled = useIsMemberAreaAppShellEnabled();

  useLayoutEffect(() => {
    if (!isMemberAreaAppShellEnabled || !ctx) return;
    ctx.requestBypass();
    return () => ctx.releaseBypass();
  }, [ctx, isMemberAreaAppShellEnabled]);
};

export const useHidePageChrome = () => {
  const ctx = useContext(ShellBypassContext);
  const isMemberAreaAppShellEnabled = useIsMemberAreaAppShellEnabled();

  useLayoutEffect(() => {
    if (!isMemberAreaAppShellEnabled || !ctx) return;
    ctx.requestHidePageChrome();
    return () => ctx.releaseHidePageChrome();
  }, [ctx, isMemberAreaAppShellEnabled]);
};
